import React, { useEffect, useRef, useState } from 'react'
import { getErrorMessage } from '@/utils/getErrorMessage'
import {
  Typography,
  Button,
  Breadcrumb,
  Layout,
  Flex,
  Card,
  Form,
  Input,
  Select,
  Spin,
  message,
} from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { type Permission, type Role } from '@/data'
import { createRole } from '@/api/roles/createRole'
import { getRoles } from '@/api/roles/getRoles'
import { getAllPermissions } from '@/api/permissions/getPermissions'
import { syncRolePermissions } from '@/api/roles/syncRolePermissions'
import { permissionTagRender } from '@/components/PermissionTag/utils'
import {
  sortOptionsByLabel,
  sortSelectedValues,
  type SelectOption,
} from '@/utils/selectSorting'
import { AuthContext } from '@/contexts/AuthContext'
import { hasPermission } from '@/auth/access'

const { Title } = Typography
const { Content } = Layout

async function findRoleById(roleId: string): Promise<Role | null> {
  let page = 1
  let lastPage = 1

  do {
    const { data, meta } = await getRoles(page)
    const role = data.find((entry) => String(entry.id) === roleId)

    if (role) {
      return role
    }

    lastPage = meta.last_page
    page += 1
  } while (page <= lastPage)

  return null
}

const RoleEditorContent: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const auth = React.useContext(AuthContext)!

  const isEditMode = Boolean(id)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')
  const [guardName, setGuardName] = useState('')
  const [permissionIds, setPermissionIds] = useState<number[]>([])
  const [permissionOptions, setPermissionOptions] = useState<SelectOption[]>([])
  const canCreateRoles = hasPermission(auth.user, 'lop:ui:action:create:roles')
  const canUpdateRoles = hasPermission(auth.user, 'lop:ui:action:update:roles')
  const canSave = isEditMode ? canUpdateRoles : canCreateRoles

  const hasFetched = useRef(false)

  useEffect(() => {
    if (hasFetched.current) return
    hasFetched.current = true
    ;(async () => {
      try {
        setLoading(true)
        const [{ data: permissions }, role] = await Promise.all([
          getAllPermissions().then((data) => ({ data })),
          id ? findRoleById(id) : Promise.resolve(null),
        ])

        const sortedOptions = sortOptionsByLabel(
          permissions.map((permission: Permission) => ({
            label: permission.name,
            value: permission.id,
          })),
        )

        setPermissionOptions(sortedOptions)

        if (!id) {
          return
        }

        if (!role) {
          message.error('Role not found')
          return
        }

        setName(role.name)
        setGuardName(role.guard_name)
        setPermissionIds(
          sortSelectedValues(
            role.permissions?.map((permission) => permission.id) ?? [],
            sortedOptions,
          ),
        )
      } catch (err) {
        message.error('Failed to load role')
        console.error(err)
      } finally {
        setLoading(false)
      }
    })()
  }, [id])

  const handleSave = async () => {
    if (!canSave) {
      return
    }

    try {
      setSaving(true)
      if (isEditMode && id) {
        await syncRolePermissions(id, {
          permission_ids: permissionIds,
        })
        message.success('Role permissions updated')
      } else {
        const created = await createRole({
          name,
          ...(guardName ? { guard_name: guardName } : {}),
        })
        await syncRolePermissions(String(created.id), {
          permission_ids: permissionIds,
        })
        message.success('Role created')
        navigate(`/admin/roles/${created.id}/edit`)
      }
    } catch (err) {
      const fallback = isEditMode
        ? 'Failed to update role permissions'
        : 'Failed to create role'
      message.error(getErrorMessage(err, fallback))
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  const handlePermissionChange = (values: number[]) => {
    setPermissionIds(sortSelectedValues(values, permissionOptions))
  }

  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    )
  }

  return (
    <div>
      <Flex vertical gap="middle" style={{ width: '100%' }}>
        <Breadcrumb
          items={[
            { title: 'Roles', href: '/admin/roles' },
            { title: isEditMode ? `Edit: ${name}` : 'New Role' },
          ]}
        />

        <Flex align="center" gap="middle">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/admin/roles')}
          />
          <Title level={3} style={{ margin: 0 }}>
            {isEditMode ? `Edit Role: ${name}` : 'Create New Role'}
          </Title>
        </Flex>

        <Flex gap="large" style={{ marginTop: 16 }}>
          <Content style={{ flex: 1, minHeight: 400 }}>
            <Card title="Role Info" style={{ maxWidth: 700 }}>
              <Form layout="vertical">
                <Form.Item label="Name">
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter name"
                    disabled={isEditMode}
                  />
                </Form.Item>
                <Form.Item label="Guard Name">
                  <Input
                    value={guardName}
                    onChange={(e) => setGuardName(e.target.value)}
                    placeholder="Optional guard name"
                    disabled={isEditMode}
                  />
                </Form.Item>
                <Form.Item label="Permissions">
                  <Select<number[]>
                    mode="multiple"
                    value={permissionIds}
                    onChange={handlePermissionChange}
                    options={permissionOptions}
                    placeholder="Select permissions"
                    optionFilterProp="label"
                    tagRender={permissionTagRender}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Form>
            </Card>
            <Flex justify="flex-end" style={{ paddingTop: 16 }}>
              {canSave ? (
                <Button type="primary" onClick={handleSave} loading={saving}>
                  {isEditMode ? 'Save Permissions' : 'Create Role'}
                </Button>
              ) : null}
            </Flex>
          </Content>
        </Flex>
      </Flex>
    </div>
  )
}

export const RoleEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  return <RoleEditorContent key={id ?? 'new'} />
}
